import React, { useState } from 'react';
import { 
  MessageSquare, 
  CheckCircle, 
  XCircle, 
  ArrowLeft,
  Settings,
  Phone,
  Mail,
  Clock,
  Shield,
  Zap,
  BarChart3,
  Users as UsersIcon,
  Calendar,
  FileText,
  Bell,
  Menu,
  X,
  Sparkles,
  AlertCircle,
  LogOut
} from 'lucide-react';
import { webhookService } from '../../services/webhookService';
import { useDeveloper } from '../../contexts/DeveloperContext';

// Componente da Logo do Telegram
const TelegramLogo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <svg 
    className={className} 
    xmlns="http://www.w3.org/2000/svg" 
    xmlnsXlink="http://www.w3.org/1999/xlink" 
    viewBox="0 0 240 240"
  >
    <defs>
      <linearGradient id="linear-gradient" x1="120" y1="240" x2="120" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#1d93d2"/>
        <stop offset="1" stopColor="#38b0e3"/>
      </linearGradient>
    </defs>
    <title>Telegram_logo</title>
    <circle cx="120" cy="120" r="120" fill="url(#linear-gradient)"/>
    <path d="M81.229,128.772l14.237,39.406s1.78,3.687,3.686,3.687,30.255-29.492,30.255-29.492l31.525-60.89L81.737,118.6Z" fill="#c8daea"/>
    <path d="M100.106,138.878l-2.733,29.046s-1.144,8.9,7.754,0,17.415-15.763,17.415-15.763" fill="#a9c6d8"/>
    <path d="M81.486,130.178,52.2,120.636s-3.5-1.42-2.373-4.64c.232-.664.7-1.229,2.1-2.2,6.489-4.523,120.106-45.36,120.106-45.36s3.208-1.081,5.1-.362a2.766,2.766,0,0,1,1.885,2.055,9.357,9.357,0,0,1,.254,2.585c-.009.752-.1,1.449-.169,2.542-.692,11.165-21.4,94.493-21.4,94.493s-1.239,4.876-5.678,5.043A8.13,8.13,0,0,1,146.1,172.5c-8.711-7.493-38.819-27.727-45.472-32.177a1.27,1.27,0,0,1-.546-.9c-.093-.469.417-1.05.417-1.05s52.426-46.6,53.821-51.492c.108-.379-.3-.566-.848-.4-3.482,1.281-63.844,39.4-70.506,43.607A3.21,3.21,0,0,1,81.486,130.178Z" fill="#fff"/>
  </svg>
);

const TelegramTestPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const [showSidebar, setShowSidebar] = useState(false);
  const { logout } = useDeveloper();


  const testWebhook = async () => {
    setIsLoading(true);
    setTestResult({ type: null, message: '' });

    try {
      const testContract = {
        id: 'teste-123',
        contratante: 'Maria Silva Santos',
        nomeAniversariante: 'João Pedro',
        dataFesta: new Date().toISOString(),
        telefone: '33999887766',
        endereco: 'Rua das Flores, 123',
        numero: '123',
        cidade: 'Uberlândia',
        estado: 'MG',
        horarioInicioFesta: '14:00',
        temaFesta: 'Super Heróis',
        observacoes: 'Festa para 20 crianças, com bolo e doces especiais',
        valor: '850,00'
      };

      // Testar Telegram diretamente
      const success = await webhookService.sendToTelegram(testContract);
      
      if (success) {
        setTestResult({
          type: 'success',
          message: 'Mensagem completa enviada para o Telegram! Verifique seu Telegram para ver todos os detalhes formatados.'
        });
      } else {
        setTestResult({
          type: 'error',
          message: 'Falha ao enviar para Telegram. Verifique se o bot está configurado corretamente.'
        });
      }
    } catch (error) {
      setTestResult({
        type: 'error',
        message: `Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const testSimpleMessage = async () => {
    setIsLoading(true);
    setTestResult({ type: null, message: '' });

    try {
      const success = await webhookService.testSimpleMessage();
      
      if (success) {
        setTestResult({
          type: 'success',
          message: 'Mensagem simples enviada! Se chegou no Telegram, o bot está funcionando.'
        });
      } else {
        setTestResult({
          type: 'error',
          message: 'Falha ao enviar mensagem simples. Verifique se o bot foi iniciado com /start.'
        });
      }
    } catch (error) {
      setTestResult({
        type: 'error',
        message: `Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const testWebhookStatus = async () => {
    setIsLoading(true);
    setTestResult({ type: null, message: '' });

    try {
      // Verificar informações do bot
      const botInfo = await webhookService.getBotInfo();
      
      if (botInfo && botInfo.ok) {
        setTestResult({
          type: 'success',
          message: `Bot funcionando! Nome: ${botInfo.result.first_name} (@${botInfo.result.username})`
        });
      } else {
        setTestResult({
          type: 'error',
          message: 'Bot não está funcionando. Verifique o token.'
        });
      }
    } catch (error) {
      setTestResult({
        type: 'error',
        message: `Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowSidebar(!showSidebar)}
                className="lg:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                     <div>
                       <h1 className="text-lg font-semibold text-slate-900">PlayKids</h1>
                       <p className="text-xs text-slate-500">Teste Telegram</p>
                     </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <a
                href="/admin/contratos"
                className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar para Contratos
              </a>
              <button
                onClick={logout}
                className="flex items-center gap-2 px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                title="Sair do modo desenvolvedor"
              >
                <LogOut className="w-4 h-4" />
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      {showSidebar && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowSidebar(false)}></div>
          <div className="fixed left-0 top-0 h-full w-64 bg-white shadow-lg">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Menu</h2>
              <button
                onClick={() => setShowSidebar(false)}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="p-4 space-y-2">
              <a href="/dashboard" className="flex items-center gap-3 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
                <BarChart3 className="w-5 h-5" />
                Dashboard
              </a>
              <a href="/calendario" className="flex items-center gap-3 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
                <Calendar className="w-5 h-5" />
                Calendário
              </a>
              <a href="/employees" className="flex items-center gap-3 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
                <UsersIcon className="w-5 h-5" />
                Funcionários
              </a>
              <a href="/lembretes" className="flex items-center gap-3 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
                <Bell className="w-5 h-5" />
                Lembretes
              </a>
              <a href="/import" className="flex items-center gap-3 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
                <FileText className="w-5 h-5" />
                Importar Contratos
              </a>
            </nav>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden lg:block lg:w-64 lg:fixed lg:inset-y-0 lg:left-0 lg:z-40">
        <div className="h-full bg-white border-r border-slate-200 flex flex-col">
          <div className="flex items-center gap-3 p-6 border-b border-slate-200">
            <div className="h-8 w-8 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
                   <div>
                     <h1 className="text-lg font-semibold text-slate-900">PlayKids</h1>
                     <p className="text-xs text-slate-500">Teste Telegram</p>
                   </div>
          </div>
          <nav className="flex-1 p-4 space-y-2">
            <a href="/dashboard" className="flex items-center gap-3 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
              <BarChart3 className="w-5 h-5" />
              Dashboard
            </a>
            <a href="/calendario" className="flex items-center gap-3 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
              <Calendar className="w-5 h-5" />
              Calendário
            </a>
            <a href="/employees" className="flex items-center gap-3 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
              <UsersIcon className="w-5 h-5" />
              Funcionários
            </a>
            <a href="/lembretes" className="flex items-center gap-3 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
              <Bell className="w-5 h-5" />
              Lembretes
            </a>
            <a href="/import" className="flex items-center gap-3 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
              <FileText className="w-5 h-5" />
              Importar Contratos
            </a>
          </nav>
        </div>
      </div>

      <div className="lg:ml-64">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header da Página */}
                 <div className="mb-8">
                   <div className="flex items-center gap-4 mb-4">
                     <div className="h-12 w-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                       <TelegramLogo className="w-7 h-7" />
                     </div>
                     <div>
                       <h1 className="text-2xl font-bold text-slate-900">Teste Telegram Bot</h1>
                       <p className="text-slate-600">Configure e teste o bot do Telegram para notificações automáticas</p>
                     </div>
                   </div>
                 </div>

          {/* Cards de Informações do Telegram */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <TelegramLogo className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Bot Token</h3>
                  <p className="text-sm text-slate-600">Token do BotFather</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm font-mono text-slate-700 break-all">
                  {webhookService.TELEGRAM_CONFIG.BOT_TOKEN.substring(0, 20)}...
                </p>
                <p className="text-xs text-slate-500 mt-1">Token configurado e ativo</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Chat ID</h3>
                  <p className="text-sm text-slate-600">Seu ID no Telegram</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-lg font-mono text-slate-900">
                  {webhookService.TELEGRAM_CONFIG.CHAT_ID}
                </p>
                <p className="text-xs text-slate-500 mt-1">ID do seu chat com o bot</p>
              </div>
            </div>
          </div>

          {/* Área de Teste */}
          <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <TelegramLogo className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900">Teste do Telegram Bot</h2>
            </div>

            {/* Botões de Teste do Telegram */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <button
                onClick={testWebhookStatus}
                disabled={isLoading}
                className="group flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Settings className="w-5 h-5 group-hover:rotate-90 transition-transform duration-200" />
                )}
                <span className="font-medium">Verificar Status</span>
              </button>

              <button
                onClick={testSimpleMessage}
                disabled={isLoading}
                className="group flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <MessageSquare className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                )}
                <span className="font-medium">Teste Simples</span>
              </button>

              <button
                onClick={testWebhook}
                disabled={isLoading}
                className="group flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <TelegramLogo className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                )}
                <span className="font-medium">Mensagem Completa</span>
              </button>
            </div>

            {/* Resultado do Teste */}
            {testResult.type && (
              <div className={`p-4 rounded-lg border ${
                testResult.type === 'success' 
                  ? 'bg-green-50 border-green-200 text-green-800' 
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.type === 'success' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <XCircle className="w-5 h-5" />
                  )}
                  <span className="font-semibold">
                    {testResult.type === 'success' ? 'Sucesso!' : 'Erro!'}
                  </span>
                </div>
                <p className="text-sm">{testResult.message}</p>
              </div>
            )}

            {/* Informações do Telegram */}
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="flex items-center gap-3 mb-6">
                <TelegramLogo className="w-6 h-6" />
                <h3 className="text-lg font-semibold text-slate-900">Configuração do Telegram</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 bg-blue-600 rounded-lg flex items-center justify-center">
                      <TelegramLogo className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-900 mb-2">Bot Configurado</h4>
                      <div className="space-y-2 text-sm text-blue-800">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>Token configurado e ativo</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>Chat ID: {webhookService.TELEGRAM_CONFIG.CHAT_ID}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>Pronto para enviar mensagens</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 bg-green-600 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-900 mb-2">Como Funciona</h4>
                      <div className="space-y-2 text-sm text-green-800">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                          <span>Cliente preenche formulário</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
                          <span>Sistema envia notificação</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
                          <span>Você recebe no Telegram</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</div>
                          <span>Resposta instantânea</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Instruções importantes */}
              <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800 mb-2">Importante: Inicie o Bot Primeiro</h4>
                    <p className="text-sm text-amber-700 mb-2">
                      Para receber mensagens, você precisa iniciar o bot no Telegram:
                    </p>
                    <ol className="text-sm text-amber-700 space-y-1 ml-4">
                      <li>1. Abra o Telegram</li>
                      <li>2. Procure por: <code className="bg-amber-100 px-1 rounded">@playkids_notifications_bot</code></li>
                      <li>3. Clique no bot e envie: <code className="bg-amber-100 px-1 rounded">/start</code></li>
                      <li>4. Aguarde a resposta do bot</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TelegramTestPage;
